import React, {
    Context,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { useDeepCompareCallback } from 'use-deep-compare';
import axios, { AxiosResponse } from 'axios';
import { uuid } from 'uuidv4';

import { filterInputToDateRange, locale, getWebpackEnvVar } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import { useToast, useLogData, useTokenModal } from '..';
import { DetectionDownloadReady } from '../../components/toastMessages';
import { USE_DOWNLOAD_DETECTIONS_HANDLER_KEY } from './constants';

const { GET_ACTIVE_LOG_POLLS } = queryStrings;

const EXPORT_DETECTIONS = `
    mutation exportDetections($filter: TableBmatDetectionFilterInput!, $export_filename: String, $export_id: String) {
        exportDetections(filter: $filter, export_filename: $export_filename, export_id: $export_id) {
            export_filename
            export_id
        }
    }
`;

interface ExportDetectionsProviderProps {
    children: ReactNode;
    ExportDetectionsContext: Context<ExportDetectionsProviderValue>;
}

/**
 * Returns data and methods for exporting detection data by using log events.
 * @param props - {@link ExportDetectionsProviderProps}
 */
export const ExportDetectionsProvider = ({
    children,
    ExportDetectionsContext,
}: ExportDetectionsProviderProps): JSX.Element => {
    const client = useApolloClient();
    const { add: addToast } = useToast();
    const { addHandler, deleteHandlers } = useLogData();
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { getTokenAsync } = useTokenModal();

    const [exportingDetections, setExportingDetections] = useState<
        ExportingDetection[]
    >([]);

    const [didShowDelayMessage, setDidShowDelayMessage] = useState(false);

    /**
     * Call the exportDetections mutation to start the backend process that generates a detection
     * download.
     */
    const exportDetections = useDeepCompareCallback(
        async (
            filter: TableBmatDetectionFilterInput,
            exportFilename: string,
        ): Promise<boolean> => {
            const dateRange = filterInputToDateRange(filter?.timestamp ?? null);

            if (dateRange.start === null || dateRange.end === null) {
                return false;
            }

            // Show the message about report exports taking a long time only once per page load
            if (!didShowDelayMessage) {
                addToast({
                    kind: 'info-banner',
                    content:
                        locale.enUS.DETECTIONS.INFO_MESSAGES
                            .DOWNLOAD_BEING_GENERATED,
                    logMessage: 'exportDetectionsBeingGenerated',
                });

                setDidShowDelayMessage(true);
            }

            const token = await getTokenAsync();

            const exportId = uuid();

            // For some reason, we get an invariant violation if we run this mutation with
            // @apollo/client's `client.query` method, so I'm using axios instead.
            return axios({
                method: 'post',
                url: trqkDirectoryUrl,
                headers: {
                    Authorization: token,
                },
                data: {
                    query: EXPORT_DETECTIONS,
                    variables: {
                        filter,
                        export_filename: exportFilename,
                        export_id: exportId,
                    },
                },
            })
                .then(
                    (
                        response: AxiosResponse<{
                            data: {
                                // AppSync assigns the name "ExportGapAnalysisResult" to this
                                // return type, even though it's for detections.
                                exportDetections: ExportGapAnalysisResult;
                            };
                        }>,
                    ) => {
                        // It's a little sketchy to pull the export_filename from the response and
                        // not the export_id, but the export_id is not available on the response
                        // so we are trusting that it was successfully saved.
                        const exportedFilename =
                            response?.data?.data?.exportDetections
                                ?.export_filename ?? null;

                        if (exportedFilename) {
                            setExportingDetections(prev => [
                                ...prev,
                                {
                                    dateRange,
                                    exportFilename: exportedFilename,
                                    exportId,
                                },
                            ]);

                            return true;
                        }

                        addToast({
                            kind: 'warning-banner',
                            content: locale.enUS.API_ERRORS.exportDetections,
                            logMessage: 'exportDetections',
                        });

                        return false;
                    },
                )
                .catch(() => {
                    addToast({
                        kind: 'warning-banner',
                        content: locale.enUS.API_ERRORS.exportDetections,
                        logMessage: 'exportDetections',
                    });

                    return false;
                });
        },
        [didShowDelayMessage, exportingDetections],
    );

    useEffect(() => {
        // When there are one or more reports being exported, ensure that log events are polled on
        // the short interval
        if (exportingDetections.length > 0) {
            client.cache.writeQuery({
                query: GET_ACTIVE_LOG_POLLS,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        activeLogPolls: {
                            __typename: 'ActiveLogPolls',
                            exportDetections: true,
                        },
                    },
                },
            });
        } else {
            client.cache.writeQuery({
                query: GET_ACTIVE_LOG_POLLS,
                data: {
                    uiState: {
                        __typename: 'UIState',
                        activeLogPolls: {
                            __typename: 'ActiveLogPolls',
                            exportDetections: false,
                        },
                    },
                },
            });
        }
    }, [exportingDetections.length]);

    /**
     * When the detection export is ready, show a toast message that prompts the user to download
     * the file.
     */
    const handleDetectionExportReady = useCallback((event: LogEvent) => {
        const logDetail = event.detail ?? null;
        const downloadUrl = logDetail?.download_url ?? null;
        const exportId = logDetail?.export_id ?? null;

        if (event.eventStatus === 'success' && downloadUrl) {
            addToast({
                kind: 'info-card',
                RenderContent: DetectionDownloadReady,
                renderContentInput: {
                    downloadUrl,
                },
                persist: true,
            });
        } else {
            addToast({
                kind: 'warning-banner',
                content: locale.enUS.LOG_EVENT_ERRORS.detectionExportReady,
                logMessage: 'detectionExportReady',
            });
        }

        if (exportId) {
            setExportingDetections(prev =>
                prev.filter(
                    exportingDetection =>
                        exportingDetection.exportId !== exportId,
                ),
            );
        }
    }, []);

    /**
     * Add event handler which is triggered when the detection export is ready.
     */
    useEffect(() => {
        addHandler({
            eventTypes: ['detectionExportReady'],
            handler: handleDetectionExportReady,
            handlerKey: USE_DOWNLOAD_DETECTIONS_HANDLER_KEY,
        });

        return () => {
            // When component unmounts, remove its log event handlers
            deleteHandlers(USE_DOWNLOAD_DETECTIONS_HANDLER_KEY);
        };
    }, [handleDetectionExportReady]);

    return (
        <ExportDetectionsContext.Provider
            value={{
                exportDetections,
                exportingDetections,
            }}
        >
            {children}
        </ExportDetectionsContext.Provider>
    );
};
